import PlusIcon from '@components/icons/PlusIcon';
import { useMediaQuery } from '@material-ui/core';
import CheckIcon from '@mui/icons-material/Check';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import VideoInstruction from '@pages/AdditionalServices/tabs/DeliveryForm/components/RoutesCalculationWidget/VideoInstruction';
import CargoSpecialConditional from '@pages/AdditionalServices/tabs/DeliveryForm/components/RoutesCalculationWidget/сargoSpecialConditional';
import {
  ContainerParameters,
  ContainerRouteCalculationParameters,
  RouteWidgetBaseParameters,
} from '@pages/AdditionalServices/tabs/DeliveryForm/types';
import useDelivery from '@pages/AdditionalServices/tabs/DeliveryForm/useDelivery';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { CargoSelection } from '../CargoSelection';
import ContainerParametersItemComponent from './ContainerParametersItemComponent';

export interface ContainerParametersProps {
  BaseData: RouteWidgetBaseParameters;
  containerParameters: ContainerRouteCalculationParameters;
  mainLanguage: string; // Текущий основной язык
  systemLanguage: string; // Системный язык
  baseConditionsSlot?: React.ReactNode;
  resetSlot?: React.ReactNode;
  copyNotificationVisible: boolean;

  handleDeleteContainerParameters(index: number): void;
  onChangeCargoFieldsFilled: { (values: boolean[]): void };
}

const ContainerParametersComponent: FC<ContainerParametersProps> = (props) => {
  const {
    baseConditionsSlot,
    resetSlot,
    handleDeleteContainerParameters,
    onChangeCargoFieldsFilled,
    containerParameters: { containerParameters: cargoParameters, isDangerousCargo },
    copyNotificationVisible,
  } = props;

  const { useRouteParams, useIsRoutesLoading, useIsLoading } = useDelivery();
  const setRouteParameter = useRouteParams().setState;
  const [isRoutesLoading] = useIsRoutesLoading().useState();
  const [isLoading] = useIsLoading().useState();
  const theme = useTheme();
  const isNotMobile = useMediaQuery(theme.breakpoints.up('sm'));
  const { t } = useTranslation('Actions');

  const handleAddNewContainer = () => {
    const { id = '', container_type = '', default_weight } = props.BaseData.containers[0];

    setRouteParameter((current) => ({
      ...current,
      containerParameters: {
        ...current.containerParameters,
        containerParameters: [
          ...current.containerParameters.containerParameters,
          {
            containerId: id,
            containerType: container_type,
            containerQuantity: 1,
            eachContainerWeight: default_weight,
            withPallets: false,
            palletsLoaded: [],
          },
        ],
      },
    }));
  };

  /**
   * Обработка изменения параметров контейнера
   * @param containerParameters
   * @param index
   * @param field
   */
  const handleChangeContainerParameters = (
    containerParameters: ContainerParameters,
    field: keyof ContainerParameters,
    index: number
  ) => {
    setRouteParameter((current) => {
      // Создание копии массива containerParameters
      const updatedContainers = [...current.containerParameters.containerParameters];
      const containerProps = props.BaseData.containers.find(
        ({ id }) => id === containerParameters.containerId
      );

      if (field === 'containerId') {
        containerParameters.eachContainerWeight = containerProps.default_weight || 10000;
        containerParameters.withPallets = false;
        containerParameters.palletsLoaded = [];
      }

      if (field === 'withPallets' && containerParameters.withPallets === false) {
        containerParameters.palletsLoaded = [];
      }

      // Обновление элемента в скопированном массиве
      updatedContainers[index] = {
        ...containerParameters,
      };

      // Возвращение обновленного состояния
      return {
        ...current,
        containerParameters: {
          ...current.containerParameters,
          containerParameters: updatedContainers,
        },
      };
    });
  };

  return (
    <>
      <CargoSelection
        isActionsDisabled={isRoutesLoading || isLoading}
        BaseData={props.BaseData}
        mainLanguage={props.systemLanguage}
        cargoSelectionType="fcl"
      />
      <section className="cargo-list">
        {cargoParameters.map((container, i) => (
          <div key={`list-item--${i}`} className={'cargo-item'}>
            <ContainerParametersItemComponent
              Position={1 + i}
              BaseData={props.BaseData}
              mainLanguage={props.mainLanguage}
              systemLanguage={props.systemLanguage}
              ContainerParameters={container}
              onChange={(params, field) => handleChangeContainerParameters(params, field, i)}
              isDeletable={cargoParameters.length > 1}
              onDelete={() => handleDeleteContainerParameters(i)}
              onChangeCargoFieldsFilled={onChangeCargoFieldsFilled}
            />
          </div>
        ))}
        <IconButton
          title={`+ ${t('Actions:Add')}`}
          className={'add-new-cargo'}
          disabled={isRoutesLoading || isLoading}
          onClick={() => handleAddNewContainer()}
        >
          <PlusIcon />
        </IconButton>
      </section>
      <div className="footer">
        <div className={'base-params'}>{baseConditionsSlot}</div>
        <CargoSpecialConditional isDangerousCargo={isDangerousCargo} />
        <div className={'right'}>
          {isNotMobile && <VideoInstruction />}
          {resetSlot && (
            <div className={'routes-calculation-widget__footer-reset-container'}>
              <>
                {copyNotificationVisible && (
                  <Alert
                    icon={<CheckIcon fontSize="inherit" />}
                    severity="success"
                    className="copy-success-alert"
                  >
                    {t('RoutesCalculationWidget:CopyCalculationLinkSuccess')}
                  </Alert>
                )}
                {resetSlot}
              </>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ContainerParametersComponent;
